/**
 * Bridge between React/Web Audio and WASM Mono Synth
 * Provides full control over MonoSynth parameters including filter and envelopes
 */

export interface MonoSynthParams {
  oscillatorType?: number; // 0=sine, 1=square, 2=saw, 3=triangle
  filterCutoff?: number; // 20 to 20000 Hz
  filterResonance?: number; // 0.5 to 20 (Q factor)
  filterEnvAmount?: number; // 0 to 1
  volume?: number; // 0.0 to 1.0
  detune?: number; // -100 to 100 cents
  portamento?: number; // seconds
  ampEnvelope?: {
    attack: number;
    decay: number;
    sustain: number;
    release: number;
  };
  filterEnvelope?: {
    attack: number;
    decay: number;
    sustain: number;
    release: number;
  };
}

export class MonoSynthBridge {
  private worklet: AudioWorkletNode | null = null;
  private context: AudioContext;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  constructor(context: AudioContext) {
    this.context = context;
  }

  async initialize(): Promise<AudioWorkletNode> {
    if (this.initPromise) {
      await this.initPromise;
      return this.worklet!;
    }

    this.initPromise = this._initialize();
    await this.initPromise;
    return this.worklet!;
  }

  private async _initialize(): Promise<void> {
    try {
      // Load AudioWorklet processor
      await this.context.audioWorklet.addModule('/wasm/mono-synth-processor.js');

      // Create AudioWorklet node
      this.worklet = new AudioWorkletNode(this.context, 'mono-synth-processor');

      // Fetch WASM bytes
      const wasmResponse = await fetch('/wasm/mono-synth.wasm');
      const wasmBytes = await wasmResponse.arrayBuffer();

      // Send WASM bytes to worklet (ArrayBuffer can be transferred)
      this.worklet.port.postMessage(
        {
          type: 'init',
          wasmBytes: wasmBytes,
        },
        [wasmBytes]
      ); // Transfer ownership for efficiency

      // Wait for initialization confirmation
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WASM Mono synth initialization timeout'));
        }, 5000);

        this.worklet!.port.onmessage = (event) => {
          if (event.data.type === 'initialized') {
            clearTimeout(timeout);
            if (event.data.success) {
              this.initialized = true;
              resolve();
            } else {
              reject(new Error(event.data.error || 'WASM Mono init failed'));
            }
          }
        };
      });

      console.log('[WASM] Mono Synth initialized successfully');
    } catch (error) {
      console.error('[WASM] Mono Synth initialization failed:', error);
      throw error;
    }
  }

  noteOn(midiNote: number, velocity: number = 100): void {
    if (!this.initialized || !this.worklet) {
      console.warn('[WASM] Mono Synth not initialized');
      return;
    }

    this.worklet.port.postMessage({
      type: 'noteOn',
      note: midiNote,
      velocity,
    });
  }

  noteOff(): void {
    if (!this.initialized || !this.worklet) {
      return;
    }

    this.worklet.port.postMessage({
      type: 'noteOff',
    });
  }

  // Oscillator Parameter

  setOscillatorType(oscType: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setOscillatorType',
      value: Math.floor(Math.max(0, Math.min(3, oscType))),
    });
  }

  // Filter Parameters

  setFilterCutoff(cutoff: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setFilterCutoff',
      value: Math.max(20, Math.min(20000, cutoff)),
    });
  }

  setFilterResonance(resonance: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setFilterResonance',
      value: Math.max(0.5, Math.min(20, resonance)),
    });
  }

  setFilterEnvAmount(amount: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setFilterEnvAmount',
      value: Math.max(0, Math.min(1, amount)),
    });
  }

  // Envelopes

  setAmpEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setAmpEnvelope',
      attack: Math.max(0.001, attack),
      decay: Math.max(0.001, decay),
      sustain: Math.max(0, Math.min(1, sustain)),
      release: Math.max(0.001, release),
    });
  }

  setFilterEnvelope(attack: number, decay: number, sustain: number, release: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setFilterEnvelope',
      attack: Math.max(0.001, attack),
      decay: Math.max(0.001, decay),
      sustain: Math.max(0, Math.min(1, sustain)),
      release: Math.max(0.001, release),
    });
  }

  // Other Parameters

  setVolume(value: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setVolume',
      value: Math.max(0, Math.min(1, value)),
    });
  }

  setDetune(cents: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setDetune',
      value: Math.max(-100, Math.min(100, cents)),
    });
  }

  setPortamento(seconds: number): void {
    if (!this.initialized || !this.worklet) return;

    this.worklet.port.postMessage({
      type: 'setPortamento',
      value: Math.max(0, seconds),
    });
  }

  // Bulk parameter update
  updateParams(params: MonoSynthParams): void {
    if (params.oscillatorType !== undefined) {
      this.setOscillatorType(params.oscillatorType);
    }
    if (params.filterCutoff !== undefined) {
      this.setFilterCutoff(params.filterCutoff);
    }
    if (params.filterResonance !== undefined) {
      this.setFilterResonance(params.filterResonance);
    }
    if (params.filterEnvAmount !== undefined) {
      this.setFilterEnvAmount(params.filterEnvAmount);
    }
    if (params.volume !== undefined) {
      this.setVolume(params.volume);
    }
    if (params.detune !== undefined) {
      this.setDetune(params.detune);
    }
    if (params.portamento !== undefined) {
      this.setPortamento(params.portamento);
    }
    if (params.ampEnvelope) {
      this.setAmpEnvelope(
        params.ampEnvelope.attack,
        params.ampEnvelope.decay,
        params.ampEnvelope.sustain,
        params.ampEnvelope.release
      );
    }
    if (params.filterEnvelope) {
      this.setFilterEnvelope(
        params.filterEnvelope.attack,
        params.filterEnvelope.decay,
        params.filterEnvelope.sustain,
        params.filterEnvelope.release
      );
    }
  }

  connect(destination: AudioNode): void {
    if (this.worklet) {
      this.worklet.connect(destination);
    }
  }

  disconnect(): void {
    if (this.worklet) {
      this.worklet.disconnect();
    }
  }

  dispose(): void {
    this.disconnect();
    this.worklet = null;
    this.initialized = false;
    this.initPromise = null;
  }

  getNode(): AudioWorkletNode | null {
    return this.worklet;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
